import React, { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { LuHash, LuSearch, LuTextSearch } from 'react-icons/lu';
import { channelLabel, messageDate } from '../../utils/sources.js';
import { Card, CardHeader } from '../ui/Card';
import AdminBadge from '../ui/AdminBadge';
import Badge from '../ui/Badge';
import EmptyState from '../ui/EmptyState';
import Highlight from '../ui/Highlight';
import { SearchInput } from '../ui/SearchInput';
import { SpinnerRing } from '../ui/Spinner';

const MIN_QUERY = 3;
const MAX_RESULTS = 200;

const searchDump = (dump, query) => {
  const workspaces = dump?.data?.workspaces;
  if (!workspaces || query.length < MIN_QUERY) return [];
  const needle = query.toLowerCase();
  const results = [];
  Object.entries(workspaces).forEach(([workspace, channels]) => {
    Object.entries(channels || {}).forEach(([channel, messages]) => {
      (Array.isArray(messages) ? messages : []).forEach((message, index) => {
        if (typeof message?.text === 'string' && message.text.toLowerCase().includes(needle)) {
          results.push({ key: `${workspace}/${channel}/${index}`, workspace, channel, message });
        }
      });
    });
  });
  return results;
};

/** Full-text search in the messages of the week (admin). Matches are highlighted, never injected. */
const DumpSearch = ({ dump }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');

  const applyQuery = useMemo(() => debounce((q) => setAppliedQuery(q.trim()), 250), []);
  useEffect(() => {
    applyQuery(searchQuery);
    return () => applyQuery.cancel();
  }, [searchQuery, applyQuery]);

  const results = useMemo(() => searchDump(dump, appliedQuery), [dump, appliedQuery]);
  const pending = searchQuery.trim() !== appliedQuery;
  const active = appliedQuery.length >= MIN_QUERY;

  return (
    <Card>
      <CardHeader
        icon={LuTextSearch}
        title="Rechercher dans les messages"
        subtitle="Tous les messages récupérés pour la semaine, photos comprises"
        badge={<AdminBadge />}
      />
      <div className="space-y-4 p-4 sm:p-5">
        <SearchInput
          size="lg"
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Rechercher un produit, une enseigne, un nom…"
          label="Rechercher dans les messages"
          helpText={searchQuery.trim().length > 0 && searchQuery.trim().length < MIN_QUERY ? `Saisissez au moins ${MIN_QUERY} caractères.` : null}
          autoFocus
        />

        {!dump?.data?.workspaces ? (
          <EmptyState icon={LuSearch} tone="gray" title="Aucune donnée à rechercher" description="Les messages de la semaine ne sont pas encore disponibles." />
        ) : pending && searchQuery.trim().length >= MIN_QUERY ? (
          <div className="flex justify-center py-6"><SpinnerRing size="sm" label="Recherche en cours" /></div>
        ) : !active ? (
          <EmptyState icon={LuSearch} tone="gray" title="Que cherchez-vous ?" description="La recherche porte sur le texte des messages, tous canaux confondus." />
        ) : results.length === 0 ? (
          <EmptyState icon={LuSearch} tone="gray" title="Aucun résultat" description={`Aucun message ne contient « ${appliedQuery} ».`} />
        ) : (
          <>
            <p className="text-sm text-gray-500 dark:text-gray-400" aria-live="polite">
              <strong className="font-semibold text-gray-900 dark:text-white">{results.length}</strong> message{results.length > 1 ? 's' : ''} trouvé{results.length > 1 ? 's' : ''}
              {results.length > MAX_RESULTS && ` — ${MAX_RESULTS} premiers affichés`}
            </p>
            <ul className="space-y-3">
              {results.slice(0, MAX_RESULTS).map(({ key, workspace, channel, message }) => {
                const date = messageDate(message.ts);
                const files = (message.files || []).filter((f) => f?.blob_url);
                return (
                  <li key={key} className="rounded-xl border border-gray-200 p-4 dark:border-gray-900">
                    <div className="mb-2 flex flex-wrap items-center gap-2 text-xs">
                      <Badge tone="violet" size="sm" icon={LuHash}>{channelLabel(channel)}</Badge>
                      <Badge tone="gray" size="sm">{workspace}</Badge>
                      <span className="font-medium text-gray-700 dark:text-gray-300">{message.username || 'Utilisateur inconnu'}</span>
                      {date && <span className="text-gray-400">· {date.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}</span>}
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800 dark:text-gray-200">
                      <Highlight text={message.text} query={appliedQuery} minLength={MIN_QUERY} />
                    </p>
                    {files.length > 0 && (
                      <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-8">
                        {files.map((file, index) => (
                          <a
                            key={file.id || file.photo_key || index}
                            href={file.blob_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block aspect-square overflow-hidden rounded-lg bg-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-night-900"
                            aria-label={`Ouvrir la photo ${index + 1} dans un nouvel onglet`}
                          >
                            <img src={file.blob_url} alt="" loading="lazy" className="h-full w-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </Card>
  );
};

export default DumpSearch;
